<template>
  <div :class="b('', modifiers)">
    <div
      v-if="useAsPlainContent"
      :class="b('title')"
    >
      <slot name="title"></slot>
    </div>
    <button
      v-else
      :id="titleId"
      type="button"
      :class="b('title')"
      :aria-expanded="!isCollapsed"
      :aria-controls="contentId"
      @click="onToggleCollapseState"
    >
      <slot name="title"></slot>
      <span
        :class="b('icon')"
        aria-hidden="true"
      >
        <e-icon
          icon="i-chevron--up"
          size="20"
        />
      </span>
    </button>
    <div
      :id="contentId"
      :class="b('content')"
      role="region"
      aria-live="polite"
    >
      <slot name="content"></slot>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, getCurrentInstance } from 'vue';
  import eIcon from '@/elements/e-icon.vue';
  import { Modifiers } from '@/plugins/vue-bem-cn/src/globals';

  type Setup = {
    uid: string;
  };

  type Data = {
    internalCollapsed: boolean;
    titleId: string;
    contentId: string;
  };

  const COLLAPSED_INITIAL_VALUE = true;

  /**
   * Renders a collapse item.
   */
  export default defineComponent({
    name: 'c-collapse',
    components: {
      eIcon,
    },

    // components: {},

    props: {
      /**
       * Initial state of the collapse content.
       */
      collapsed: {
        type: Boolean,
        default: COLLAPSED_INITIAL_VALUE,
      },

      /**
       * Use header and content as plain content. Useful if you want to display the collapse only on eg. mobile.
       */
      useAsPlainContent: {
        type: Boolean,
        default: false,
      },
    },
    // emits: [],

    setup(): Setup {
      return {
        uid: (getCurrentInstance()?.uid || Math.random().toString(36).slice(2, 15)) as string,
      };
    },
    data(): Data {
      return {
        internalCollapsed: COLLAPSED_INITIAL_VALUE,
        titleId: `c-collapse-title-${this.uid}`,
        contentId: `c-collapse-title-${this.uid}`,
      };
    },

    computed: {
      modifiers(): Modifiers {
        const { useAsPlainContent, isCollapsed } = this;

        return {
          useAsPlainContent,
          isCollapsed,
        };
      },

      isCollapsed(): boolean {
        return !this.useAsPlainContent && this.internalCollapsed;
      },
    },
    watch: {
      collapsed(value: boolean) {
        this.internalCollapsed = value;
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      this.internalCollapsed = this.collapsed;
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      onToggleCollapseState() {
        if (!this.useAsPlainContent) {
          this.internalCollapsed = !this.internalCollapsed;
        }
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .c-collapse {
    $this: &;

    &:not(&--use-as-plain-content) {
      #{$this}__title {
        cursor: pointer;
      }
    }

    &--is-collapsed {
      #{$this}__content {
        display: none;
      }

      #{$this}__icon {
        transform: rotate(-180deg);
      }
    }

    &__title {
      display: flex;
      justify-content: space-between;
      width: 100%;
      gap: 5px;
      overflow-wrap: anywhere;
    }

    &__icon {
      display: flex;
      align-items: center;
    }

    &__content {
      overflow: hidden;
    }
  }
</style>
